import { useState } from 'react';
import { DeploymentInfo } from '../types/types';

type DeploymentInfoCache = {
  [contextId: string]: DeploymentInfo;
};

let deploymentInfoCache: DeploymentInfoCache = {};

export const useDeploymentInfoCache = () => {
  const [cache, setCache] = useState<DeploymentInfoCache>(deploymentInfoCache);

  const getFromCache = (contextId: string): DeploymentInfo | null => {
    return deploymentInfoCache[contextId] || null;
  };

  const addToCache = (contextId: string, info: DeploymentInfo): void => {
    deploymentInfoCache = {
      ...deploymentInfoCache,
      [contextId]: info,
    };

    setCache(deploymentInfoCache);
  };

  const removeFromCache = (contextId: string): void => {
    const { [contextId]: removed, ...rest } = deploymentInfoCache;
    deploymentInfoCache = rest;
    setCache(deploymentInfoCache);
  };

  const clearCache = (): void => {
    deploymentInfoCache = {};
    setCache({});
  };

  return {
    cache,
    getFromCache,
    addToCache,
    removeFromCache,
    clearCache,
  };
};

export const prefetchDeploymentInfo = (
  contextIds: string[],
  resources: any[],
  applicationsNodes: any[],
): void => {
  if (!contextIds?.length || !resources?.length || !applicationsNodes?.length) {
    return;
  }

  try {
    const resourcesByContextId = resources.reduce((acc, resource) => {
      const contextId = resource.sitecoreContextId;
      if (contextId) {
        if (!acc[contextId]) {
          acc[contextId] = [];
        }
        acc[contextId].push(resource);
      }
      return acc;
    }, {});

    contextIds.forEach((contextId) => {
      if (deploymentInfoCache[contextId]) {
        return;
      }

      const contextResources = resourcesByContextId[contextId];
      if (!contextResources) {
        return;
      }

      const findDeploymentInfo = () => {
        for (const resource of contextResources) {
          if (!resource.tenantId) {
            continue;
          }

          const tenantNode = applicationsNodes.find(
            (node) => node.id === resource.tenantId,
          );
          if (!tenantNode || !tenantNode.annotations) {
            continue;
          }

          const projectId = tenantNode.annotations.find(
            (a: { key: string; value: string }) =>
              a.key === 'XMCloud.ProjectId' ||
              a.key === 'XmCloud.ProjectId' ||
              (a.key.toLowerCase().includes('project') &&
                a.key.toLowerCase().includes('id')),
          )?.value;

          const environmentId = tenantNode.annotations.find(
            (a: { key: string; value: string }) =>
              a.key === 'XMCloud.EnvironmentId' ||
              a.key === 'XmCloud.EnvironmentId' ||
              (a.key.toLowerCase().includes('environment') &&
                a.key.toLowerCase().includes('id')),
          )?.value;

          if (projectId && environmentId) {
            return { projectId, environmentId };
          }
        }

        return null;
      };

      const info = findDeploymentInfo();
      if (info) {
        deploymentInfoCache[contextId] = {
          projectId: info.projectId,
          environmentId: info.environmentId,
          isLoading: false,
          error: null,
        };
      }
    });
  } catch (error) {
    console.error('Error in prefetchDeploymentInfo:', error);
  }
};
